import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { MapPin, ChevronRight, Edit, Trash2, Camera } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import { ResearchAssistantPanel } from "@/components/ResearchAssistantPanel"
import { ImageGallery, Lightbox, type GalleryImage } from "@/components/ImageGallery"
import { PhotosTab } from "@/components/PhotosTab"
import { RequestDeletionButton } from "@/components/RequestDeletionButton"
import { SimpleFormat } from "@/components/SimpleFormat"
import { formatGigDate, type GigDatePrecision } from "@/lib/gigDates"

type GigAct = {
  id: number
  name: string
  slug: string | null
  type: string
  notes: string | null
  source: string | null
}

type GigShow = {
  date: string
  date_precision: GigDatePrecision
  end_date: string | null
  acts: GigAct[]
  images: GalleryImage[]
}

type GigYear = {
  year: number | null
  shows: GigShow[]
}

type VenueDetail = {
  id: number
  name: string
  slug: string
  address: string | null
  city: string
  state: string
  post_code: string | null
  region_name: string | null
  notes: string | null
  years_open: string | null
  in_amh: boolean
  gigs_count: number
  primary_image: { url: string; og_image_url: string; alt_text: string } | null
  images: GalleryImage[]
  gigs: GigYear[]
}

type Permissions = { can_edit: boolean; can_delete: boolean; can_request_deletion: boolean }

type Tab = "gigs" | "photos"

const ALL_TABS: { id: Tab; label: string }[] = [
  { id: "gigs",   label: "Gigs" },
  { id: "photos", label: "Photos" },
]

function GigsTab({ gigs, canEdit, returnTo }: { gigs: GigYear[]; canEdit: boolean; returnTo: string }) {
  const [ expandedYears, setExpandedYears ] = useState<Set<number | null>>(
    () => new Set(gigs.slice(-3).map((g) => g.year))
  )
  const [ gigLightbox, setGigLightbox ] = useState<{ images: GalleryImage[]; startIndex: number } | null>(null)

  if (gigs.length === 0) {
    return <p className="text-sm text-stone-500 dark:text-stone-400 py-4">No gigs on record yet.</p>
  }

  const totalShows = gigs.reduce((n, yr) => n + yr.shows.length, 0)

  function toggle(year: number | null) {
    setExpandedYears((prev) => {
      const next = new Set(prev)
      next.has(year) ? next.delete(year) : next.add(year)
      return next
    })
  }

  return (
    <div>
      <p className="text-xs text-stone-500 dark:text-stone-400 mb-4 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
        {totalShows} show{totalShows !== 1 ? "s" : ""} across {gigs.length} year{gigs.length !== 1 ? "s" : ""}
      </p>
      <div className="divide-y divide-stone-100 dark:divide-stone-800/60">
        {gigs.map(({ year, shows }) => {
          const open = expandedYears.has(year)
          return (
            <div key={year ?? "unknown"}>
              <button
                onClick={() => toggle(year)}
                className="w-full flex items-center gap-2 py-3 text-left hover:bg-stone-50 dark:hover:bg-stone-800/40 -mx-1 px-1 rounded transition-colors"
              >
                <ChevronRight className={`w-3.5 h-3.5 text-stone-400 shrink-0 transition-transform duration-150 ${open ? "rotate-90" : ""}`} />
                <span className="text-sm font-semibold text-stone-900 dark:text-white tabular-nums" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                  {year ?? "Year unknown"}
                </span>
                <span className="text-xs text-stone-500 dark:text-stone-400 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                  · {shows.length} show{shows.length !== 1 ? "s" : ""}
                </span>
              </button>

              {open && (
                <div className="mb-2 divide-y divide-stone-50 dark:divide-stone-800/40 ml-5">
                  {shows.map((show) => (
                    <div key={show.date} className="py-2.5 flex items-start gap-3">
                      <span className="text-xs text-stone-500 dark:text-stone-400 shrink-0 w-20 tabular-nums mt-0.5" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                        {formatGigDate(show.date, show.end_date, show.date_precision)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex gap-3">
                          {show.images.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setGigLightbox({ images: show.images, startIndex: 0 })}
                              className="relative shrink-0 w-20 h-16 rounded overflow-hidden bg-stone-100 dark:bg-stone-800 hover:opacity-80 transition-opacity"
                              aria-label="View gig photo"
                            >
                              <img src={show.images[0].thumb_url ?? undefined} alt="Gig photo" className="w-full h-full object-cover" />
                              {show.images.length > 1 && (
                                <span className="absolute bottom-0.5 right-0.5 text-[9px] font-medium bg-black/60 text-white rounded px-1 leading-4">
                                  {show.images.length}
                                </span>
                              )}
                            </button>
                          )}
                          <div className="flex-1 min-w-0">
                            {show.acts.map((act, i) => (
                              <span key={act.id} className="inline-flex items-center gap-1">
                                {i > 0 && <span className="text-stone-400 dark:text-stone-500">, </span>}
                                {act.slug ? (
                                  <Link
                                    href={`/${act.type === "Band" ? "bands" : "people"}/${act.slug}`}
                                    className="text-sm font-medium text-stone-900 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline"
                                    style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                                  >
                                    {act.name}
                                  </Link>
                                ) : (
                                  <span className="text-sm font-medium text-stone-900 dark:text-white" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                                    {act.name}
                                  </span>
                                )}
                                {canEdit && (
                                  <Link
                                    href={`/admin/image_attachments/new?attachable_type=Gig&attachable_id=${act.id}&return_to=${encodeURIComponent(returnTo)}`}
                                    className="text-stone-300 hover:text-amber-500 dark:text-stone-600 dark:hover:text-amber-400 transition-colors no-underline"
                                    aria-label="Attach photo to this gig"
                                  >
                                    <Camera className="w-3 h-3" />
                                  </Link>
                                )}
                              </span>
                            ))}
                            {show.acts.length === 1 && show.acts[0].notes && (
                              <p className="text-xs text-stone-400 dark:text-stone-500 mt-0.5 italic">{show.acts[0].notes}</p>
                            )}
                            {show.acts.length === 1 && show.acts[0].source && (
                              <p className="text-xs text-stone-400 dark:text-stone-500 mt-0.5">Source: {show.acts[0].source}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {gigLightbox && (
        <Lightbox images={gigLightbox.images} startIndex={gigLightbox.startIndex} onClose={() => setGigLightbox(null)} />
      )}
    </div>
  )
}

export default function VenueShow({ venue, permissions }: { venue: VenueDetail; permissions: Permissions }) {
  const tabCounts: Record<Tab, number> = {
    gigs:   venue.gigs.length,
    photos: venue.images.length,
  }
  const visibleTabs = ALL_TABS.filter((t) => tabCounts[t.id] > 0 || permissions.can_edit)
  const [ activeTab, setActiveTab ] = useState<Tab>(() => visibleTabs[0]?.id ?? "gigs")

  const metaTitle = `${venue.name} — ${venue.city}, ${venue.state}`
  const metaDescription = `${venue.name} in ${venue.city}, ${venue.state}${venue.gigs_count > 0 ? ` · ${venue.gigs_count} gigs` : ""}`

  return (
    <PublicShell>
      <Head title={metaTitle}>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        {venue.primary_image?.og_image_url && <meta property="og:image" content={venue.primary_image.og_image_url} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {/* Breadcrumb */}
        <div className="text-xs text-stone-400 dark:text-stone-500 mb-6 flex items-center gap-1.5">
          <Link href="/venues" className="hover:text-amber-600 dark:hover:text-amber-400 no-underline">Venues</Link>
          <span>/</span>
          <span className="text-stone-600 dark:text-stone-300">{venue.name}</span>
        </div>

        {/* Hero */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-6 mb-8">
          <ImageGallery images={venue.images ?? []} />

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 mb-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 dark:text-white leading-tight tracking-tight" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                {venue.name}
              </h1>

              {(permissions.can_edit || permissions.can_delete || permissions.can_request_deletion) && (
                <div className="flex items-center gap-2 shrink-0">
                  {permissions.can_edit && (
                    <Link
                      href={`/venues/${venue.slug}/edit?return_to=/venues/${venue.slug}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline"
                    >
                      <Edit className="w-3.5 h-3.5" />
                      Edit
                    </Link>
                  )}
                  {permissions.can_delete && (
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Delete "${venue.name}"? This cannot be undone.`)) {
                          router.delete(`/venues/${venue.slug}`)
                        }
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-950/60 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  )}
                  {permissions.can_request_deletion && (
                    <RequestDeletionButton type="Venue" id={venue.id} name={venue.name} />
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5 text-stone-500 dark:text-stone-400 mb-3">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="text-sm">
                {venue.city}, {venue.state}
                {venue.region_name && ` · ${venue.region_name}`}
              </span>
            </div>

            <div className="space-y-1 text-sm mb-3">
              {venue.years_open && (
                <p className="text-stone-700 dark:text-stone-300">Open {venue.years_open.replace(/^From/, "from").replace(/^Until/, "until")}</p>
              )}
              {venue.address && (
                <p className="text-stone-600 dark:text-stone-400">{venue.address}</p>
              )}
              {venue.post_code && (
                <p className="text-stone-500 text-xs tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                  {venue.post_code}
                </p>
              )}
            </div>

            {venue.notes && (
              <div className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed mb-3 [&_p]:mb-2 [&_p:last-child]:mb-0">
                <SimpleFormat text={venue.notes} />
              </div>
            )}

            <div className="flex flex-wrap gap-4 text-xs text-stone-500 dark:text-stone-400" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
              {venue.gigs_count > 0 && (
                <span>{venue.gigs_count} gig{venue.gigs_count !== 1 ? "s" : ""}</span>
              )}
              {venue.in_amh && (
                <span className="text-amber-600 dark:text-amber-400 font-medium">In AMH</span>
              )}
            </div>
          </div>
        </div>

        {/* Tab bar */}
        {visibleTabs.length > 0 && (
          <div className="border-b border-stone-200 dark:border-stone-800 mb-6 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
            <div className="flex gap-0 -mb-px overflow-x-auto">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={[
                    "px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors focus-visible:outline-none",
                    activeTab === tab.id
                      ? "border-amber-500 text-amber-600 dark:text-amber-400"
                      : "border-transparent text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white hover:border-stone-300 dark:hover:border-stone-600",
                  ].join(" ")}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === "gigs" && <GigsTab gigs={venue.gigs} canEdit={permissions.can_edit} returnTo={`/venues/${venue.slug}`} />}

        {activeTab === "photos" && (
          <PhotosTab
            images={venue.images}
            adminControls={permissions.can_edit ? {
              setPrimaryBasePath: "/admin/image_attachments",
              removeBasePath:     "/admin/image_attachments",
              deleteBasePath:     "/admin/image_attachments",
              addImageHref:       `/admin/images/new?attachable_type=Venue&attachable_id=${venue.id}&return_to=/venues/${venue.slug}`,
              attachExistingHref: `/admin/image_attachments/new?attachable_type=Venue&attachable_id=${venue.id}&return_to=/venues/${venue.slug}`,
              returnTo:           `/venues/${venue.slug}`,
            } : undefined}
          />
        )}
      </div>

      {permissions.can_edit && (
        <ResearchAssistantPanel entityType="Venue" entityId={venue.id} />
      )}
    </PublicShell>
  )
}
